import * as Sentry from '@sentry/react';
import { createFileRoute, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { type AssetClass, queryKeys, useGetCollectionQuery } from '@vizit/api';
import {
  type CollectionAssetSortOption,
  DEFAULT_ASSET_SORT,
  parseCollectionAssetSort,
  parseCollectionClassification,
  parseFavoritesOnly,
  parseSparkHidden,
} from '@vizit/collections';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { buildDynamicBreadcrumbs } from '@vizit/utils';
import { resolveLoaderWorkspaceId, useActiveWorkspaceId } from '../../../../../hooks/use-workspace-link';
import { CollectionDetailsPage, PagesProvider } from '../../../../../pages';
import { LIBRARY_BREADCRUMB, beforeLoadAuthenticatedValidation } from '../../../../../router/route-utils';
import { BreadcrumbsPageTemplate } from '../../../../../templates';

export const Route = createFileRoute('/_authenticated/w/$workspaceSlug/library/collections/$collectionId')({
  staticData: {
    pageName: 'Collection Details',
  },
  beforeLoad: ({ context, location }) => beforeLoadAuthenticatedValidation(context, location),
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): { sort?: CollectionAssetSortOption; spark?: 'hidden'; fav?: 'only'; assetClass?: AssetClass } => ({
    sort: parseCollectionAssetSort(search.sort),
    spark: parseSparkHidden(search.spark) ? 'hidden' : undefined,
    fav: parseFavoritesOnly(search.fav) ? 'only' : undefined,
    assetClass: parseCollectionClassification(search.assetClass),
  }),
  loader: async ({ location, params, context }) => {
    const workspaceId = resolveLoaderWorkspaceId(context, params.workspaceSlug);

    let collectionName = 'Collection Details';

    if (workspaceId) {
      try {
        const collectionData = await context.queryClient.ensureQueryData({
          queryKey: queryKeys.collections.detail({
            workspace_id: workspaceId,
            collection_id: params.collectionId,
          }).queryKey,
          queryFn: () =>
            useGetCollectionQuery.fetcher({
              workspace_id: workspaceId,
              collection_id: params.collectionId,
            })(),
        });

        collectionName = collectionData?.name ?? 'Collection Details';
      } catch (error) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Navigation,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Navigation].breadcrumbs,
          },
          extra: {
            page: 'collection details',
          },
        });
      }
    }

    const breadcrumbs = buildDynamicBreadcrumbs(
      location.state?.from,
      collectionName,
      // Current page is not clickable, so we don't need to pass href
      undefined,
      location.search,
      LIBRARY_BREADCRUMB,
    );

    return { breadcrumbs };
  },
});

function RouteComponent() {
  const { collectionId } = useParams({ from: Route.id });
  const { auth } = Route.useRouteContext();
  const router = useRouter();
  const search = useSearch({ from: Route.id });

  const workspaceId = useActiveWorkspaceId() ?? undefined;
  const sortOption = search.sort ?? DEFAULT_ASSET_SORT;
  const hideSparkImages = search.spark === 'hidden';
  const favoritesOnly = search.fav === 'only';
  const classification = search.assetClass;

  const handleSortOptionChange = (option: CollectionAssetSortOption) => {
    router.navigate({
      to: Route.fullPath,
      params: { collectionId },
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        sort: option === DEFAULT_ASSET_SORT ? undefined : option,
      }),
    });
  };

  const handleSparkVisibilityChange = (hide: boolean) => {
    router.navigate({
      to: Route.fullPath,
      params: { collectionId },
      search: (prev: Record<string, unknown>) => ({ ...prev, spark: hide ? 'hidden' : undefined }),
    });
  };

  const handleFavoritesOnlyChange = (favorites: boolean) => {
    router.navigate({
      to: Route.fullPath,
      params: { collectionId },
      search: (prev: Record<string, unknown>) => ({ ...prev, fav: favorites ? 'only' : undefined }),
    });
  };

  const handleClassificationChange = (nextClassification: AssetClass | null) => {
    router.navigate({
      to: Route.fullPath,
      params: { collectionId },
      search: (prev: Record<string, unknown>) => ({ ...prev, assetClass: nextClassification ?? undefined }),
    });
  };

  return (
    <PagesProvider template={BreadcrumbsPageTemplate} templateProps={{ breadcrumbActionsPosition: 'inline' }}>
      <CollectionDetailsPage
        collectionId={collectionId}
        workspaceId={workspaceId}
        organizationId={auth?.organizationId ?? undefined}
        sortOption={sortOption}
        hideSparkImages={hideSparkImages}
        favoritesOnly={favoritesOnly}
        classification={classification}
        onSortOptionChange={handleSortOptionChange}
        onSparkVisibilityChange={handleSparkVisibilityChange}
        onFavoritesOnlyChange={handleFavoritesOnlyChange}
        onClassificationChange={handleClassificationChange}
      />
    </PagesProvider>
  );
}
